<template>
	<main class="hosted-invoice-page" :style="canvasStyle">
		<div v-if="!invoice || !account" class="invoice-loading" role="status">
			<v-progress-circular indeterminate :size="36" :width="3" color="primary"></v-progress-circular>
		</div>

		<div v-else class="hosted-invoice-shell">
			<header class="hosted-invoice-toolbar d-print-none">
				<div class="invoice-identity">
					<span class="invoice-mark" aria-hidden="true">
						<portal-icon name="receipt-text" :size="20"></portal-icon>
					</span>
					<div>
						<span>{{ $t('invoices.list.invoice-number') }}</span>
						<strong>{{ invoice.invoiceNumberFormatted }}</strong>
					</div>
				</div>

				<div class="viewer-actions">
					<div class="language-control" v-if="rendererType === 'InvoiceRenderer'">
						<language-switcher></language-switcher>
					</div>
					<button type="button" class="download-action" @click="downloadPdf">
						<portal-icon name="download" :size="18"></portal-icon>
						<span>Download PDF</span>
					</button>
				</div>
			</header>

			<section class="hosted-document-surface">
				<component
					class="hosted-invoice-document"
					:is="rendererType"
					:invoice="invoice"
					:scheduled-payments="invoice.scheduledPayments"
					:account="account"
					:edit-mode="false"
					:key="refreshKey"
					:attachment-service="invoiceService"
					:invoice-service="invoiceService"
					:appearance="effectiveAppearance"
					@pay-now="goToPaymentPage"
				></component>
			</section>
		</div>
	</main>
</template>

<script>
	import qs from 'qs';
	import HttpClient from '@/services/HttpClient';
	import InvoiceRenderer from '@/modules/invoices/InvoiceRenderer/InvoiceRenderer';
	import InvoiceService from '@/modules/invoices/viewer/InvoiceService';
	import LanguageSwitcher from '@/components/LanguageSwitcher';
	import InvoiceRendererV2 from '@/modules/invoices/invoiceRendererV2/InvoiceRendererV2';
	import PortalIcon from '@/components/PortalIcon.vue';
	import { invoiceStyles } from '@moxie/branding';
	import { portalBrandStyle, resolveDocumentBranding } from '@/utils/portalBranding';

	export default {
		name: 'InvoiceViewer',

		props: ['id'],

		components: { InvoiceRenderer, InvoiceRendererV2, LanguageSwitcher, PortalIcon },

		data: function() {
			return {
				refreshKey: 1,
				invoice: null,
				account: null,
				invoiceService: null,
				token: null,
				httpClient: null,
				capturedBranding: null,
			};
		},

		mounted() {
			this.$vuetify.theme.dark = false;
			let podUrl;

			if (this.id) {
        podUrl = this.$store.getters.getPodUrl;
        this.httpClient = new HttpClient(this.$store.state.domain.podUrl, false);
        this.token = this.id;
			} else {
				let queryString = qs.parse(self.location.search.slice(1));
				let request = JSON.parse(atob(queryString.token.split('.')[0]));

				podUrl = request.podUrl;

				this.httpClient = new HttpClient(podUrl, false);
				this.token = queryString.token;
			}

			this.invoiceService = new InvoiceService(this.token, podUrl);

			this.httpClient
				.get('/api/docs/invoice', {
					params: {
						token: this.token,
					},
				})
				.then((res) => {
					this.invoice = res.data.invoice;
					this.account = res.data.account;
					this.capturedBranding = this.invoice.invoiceTemplate && this.invoice.invoiceTemplate.branding;
					this.activateDocumentBranding();
					this.applyDocumentBranding();
          this.$store.state.clientFields = res.data.clientFields;
          this.$store.state.contactFields = res.data.contactFields;
				})
				.catch((err) => {
          console.log(err);
					this.$router.push('/404');
				});
		},

		beforeDestroy() {
			this.$store.commit('setDocumentBranding', null);
			this.$store.state.eventBus.$emit('document-branding-changed', this.$store.state.darkMode);
		},

		methods: {
			activateDocumentBranding() {
				this.$store.commit('setDocumentBranding', this.capturedBranding || false);
				this.$store.state.eventBus.$emit('document-branding-changed', this.$store.state.darkMode);
			},

			applyDocumentBranding() {
				if (!this.capturedBranding || !this.invoice || !this.invoice.invoiceTemplate) return;
				this.$set(
					this.invoice.invoiceTemplate,
					'branding',
					resolveDocumentBranding(this.capturedBranding, this.effectiveAppearance)
				);
			},
			goToPaymentPage: function() {
				if (!this.invoice.payOnlineUrl) return;
				const token = new URL(this.invoice.payOnlineUrl).searchParams.get('token');
				window.open(window.location.origin + '/payment?token=' + token, '_blank');
			},

			downloadPdf: function() {
				this.$store.commit('startLoading');
				this.invoiceService
					.downloadInvoice(this.effectiveAppearance)
					.then((res) => {
						let fileURL = window.URL.createObjectURL(new Blob([res.data]));

						let fileLink = document.createElement('a');
						fileLink.href = fileURL;
						fileLink.setAttribute('download', 'Invoice-' + this.invoice.invoiceNumberFormatted + '.pdf');
						document.body.appendChild(fileLink);
						fileLink.click();
					})
					.finally(() => this.$store.commit('stopLoading'));
			},
		},

		computed: {
			effectiveAppearance() {
				return this.$vuetify.theme.dark ? 'dark' : 'light';
			},
			canvasStyle() {
				if (!this.invoice || !this.invoice.invoiceTemplate) return {};
				const styles = invoiceStyles(this.invoice.invoiceTemplate);
				return {
					...portalBrandStyle(resolveDocumentBranding(this.capturedBranding, this.effectiveAppearance)),
					background: styles.backgroundColor,
					'--invoice-document-width': `${styles.maxWidth}px`,
				};
			},
			rendererType: function() {
				return this.invoice && this.invoice.invoiceTemplate ? 'InvoiceRendererV2' : 'InvoiceRenderer';
			},
		},

		watch: {
			'$vuetify.theme.dark'() {
				this.applyDocumentBranding();
			},
		},
	};
</script>

<style scoped lang="scss">
	.hosted-invoice-page {
		width: 100%;
		min-height: 100vh;
		overflow: auto;
		background:
			radial-gradient(circle at 50% 0%, color-mix(in srgb, var(--v-primary-base) 7%, transparent), transparent 34rem),
			var(--v-gray_20-base);
		color: var(--portal-text);
		text-align: left;
	}

	.invoice-loading {
		min-height: 100vh;
		display: grid;
		place-items: center;
	}

	.hosted-invoice-shell {
		width: min(calc(var(--invoice-document-width, 1136px) + 44px), 100%);
		margin: 0 auto;
		padding: 22px;
	}

	.hosted-invoice-toolbar {
		min-height: 64px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 18px;
		margin-bottom: 14px;
		padding: 10px 12px 10px 16px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 16px);
		background: color-mix(in srgb, var(--portal-surface) 94%, transparent);
		box-shadow: 0 12px 34px rgba(16, 17, 15, 0.08);
		backdrop-filter: blur(14px);
	}

	.invoice-identity,
	.viewer-actions {
		min-width: 0;
		display: flex;
		align-items: center;
	}

	.invoice-identity {
		gap: 11px;
	}

	.invoice-mark {
		width: 38px;
		height: 38px;
		flex: 0 0 38px;
		border-radius: 11px;
	}

	.invoice-mark {
		display: grid;
		place-items: center;
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.invoice-identity > div {
		min-width: 0;
		display: flex;
		flex-direction: column;
	}

	.invoice-identity span {
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 600;
	}

	.invoice-identity strong {
		overflow: hidden;
		color: var(--portal-text);
		font-size: 14px;
		font-weight: 680;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.viewer-actions {
		gap: 8px;
	}

	.language-control {
		max-width: 170px;
	}

	.download-action {
		min-height: 42px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 0 14px;
		border: 0;
		border-radius: var(--brand-button-radius, 11px);
		background: var(--brand-button-fill, var(--v-primary-base));
		color: var(--brand-button-solidText, var(--v-white-base));
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		cursor: pointer;
	}

	.hosted-document-surface {
		min-width: 0;
	}

	.hosted-invoice-document ::v-deep .main-form {
		padding: 0 !important;
		background: transparent !important;
	}

	.hosted-invoice-document {
		min-height: calc(100vh - 124px);
	}

	@media (max-width: 599px) {
		.hosted-invoice-shell {
			padding: 0;
		}

		.hosted-invoice-toolbar {
			position: sticky;
			top: 0;
			z-index: 4;
			min-height: 58px;
			margin: 0;
			padding: 8px 10px;
			border-width: 0 0 1px;
			border-radius: 0;
		}

		.invoice-mark {
			width: 34px;
			height: 34px;
			flex-basis: 34px;
		}

		.language-control {
			display: none;
		}

		.download-action {
			width: 40px;
			height: 40px;
			min-height: 40px;
			padding: 0;
			border-radius: var(--brand-button-radius, 50%);
		}

		.download-action span {
			display: none;
		}

	}

	@media print {
		.hosted-invoice-page,
		.hosted-invoice-shell {
			padding: 0;
			background: white;
		}

	}
</style>
